// Step 27 Option B: shared approve-moment hook.
// Parallel to useReject, but approval is uniformly positive — no reason
// taxonomy. One-click flow:
//   - hook.start(moment) → POSTs immediately (no dialog)
//   - undoToast appears bottom-right for 8s
//   - hook.undo() reverses
//
// Surfaces wire it the same way useReject does. Caller passes
// onLocalMark / onLocalUnmark so the surface can show a "✓ approved"
// affordance without refetching.

import { useEffect, useState } from 'react'
import { useHighlightsApi } from './useHighlightsApi'

export function useApprove({ onLocalMark, onLocalUnmark } = {}) {
  const api = useHighlightsApi()
  const [undoToast, setUndoToast] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!undoToast) return
    const t = setTimeout(() => setUndoToast(null), 8000)
    return () => clearTimeout(t)
  }, [undoToast])

  async function start(m) {
    if (!m || !m.tbtv_id || typeof m.start_ms !== 'number') {
      console.warn('useApprove.start: moment missing tbtv_id or start_ms', m)
      return
    }
    if (busy) return
    setBusy(true)
    onLocalMark?.(m)
    try {
      const res = await api.approveMoment({
        tbtv_id: m.tbtv_id, start_ms: m.start_ms, end_ms: m.end_ms ?? null,
        moment_type: m.moment_type ?? null, title: m.title || '',
        source: m.source ?? null, detector: m.detector ?? null,
        note: null,
      })
      const label = `${m.title || '(untitled)'} @ ${formatMs(m.start_ms)}`
      setUndoToast({
        approval_id: res.approval_id, label, moment: m,
        duplicate: !!res.duplicate,
      })
    } catch (e) {
      onLocalUnmark?.(m)
      // eslint-disable-next-line no-alert
      window.alert(`Approve failed: ${e.message || e}`)
    } finally {
      setBusy(false)
    }
  }

  async function undo() {
    if (!undoToast) return
    const { approval_id, moment } = undoToast
    setUndoToast(null)
    onLocalUnmark?.(moment)
    try {
      await api.undoApprovedMoment(approval_id)
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Undo failed: ${e.message || e}`)
    }
  }

  return { start, undo, undoToast, busy }
}

function formatMs(ms) {
  const totalS = Math.floor((ms || 0) / 1000)
  const h = Math.floor(totalS / 3600)
  const m = Math.floor((totalS % 3600) / 60)
  const s = totalS % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}
